import React, { useMemo } from 'react';
import styled, { css } from 'styled-components';

import { publish, SubscriptionTopic } from '../../../../viewer/messaging/pubsub';
import { DesignTool } from '../../../../viewer/tools/design-tool';
import InspectionTool from '../../../../viewer/tools/inspection-tool';
import { OnDemandTool } from '../../../../viewer/tools/on-demand-tool';
import { OrthophotoComparisonTool } from '../../../../viewer/tools/orthophoto-comparison-tool';
import { ReferencePointTransformTool } from '../../../../viewer/tools/reference-point-transform-tool';
import { ReportTool } from '../../../../viewer/tools/report-tool';
import { VolumeCalculatorTool } from '../../../../viewer/tools/volume-calculator-tool';
import { VolumeInspectorTool } from '../../../../viewer/tools/volume-inspector-tool';
import { WmsTool } from '../../../../viewer/tools/wms-tool';
import { reset } from '../../../utils/styled-reset';
import { LanguageProvider } from '../../language-provider/language-provider';
import { CompareOrthophotosMenu } from '../../skyview-new/components/compare-orthophotos-menu/compare-orthophotos-menu';
import { CutFillMap } from '../../skyview-new/components/cut-fill-map/cut-fill-map';
import { OnDemandMenu } from '../../skyview-new/components/on-demand/on-demand-menu';
import { ReportToolMenu } from '../../skyview-new/components/report-tool-menu/report-tool-menu';
import { VolumeCalculationTool } from '../../skyview-new/components/volume-calculation-tool/volume-calculation-tool';
import { WmsToolMenu } from '../../skyview-new/components/wms-tool-menu';
import { SkyviewContext } from '../skyview-state';
import { useIntercomWidgetPositioner } from './hooks/use-intercom-widget-positioner';
import { useRightMenuWindow } from './hooks/use-right-menu-window';
import { DesignToolMenu } from './tools/design-tool-menu/design-tool-menu';
import { InspectionToolMenu } from './tools/inspection-tool-menu/inspection-tool-menu';
import { ReferencePointTransform } from './tools/reference-point-transform/reference-point-transform';

const rightMenuWidth = 300;

type Props = {
  className?: string;
};

const RightMenuAutoExpandContainer = (props: Props) => {
  const { activeTool } = React.useContext(SkyviewContext);
  const ref = useIntercomWidgetPositioner<HTMLDivElement>();

  const content = useMemo(() => {
    if (activeTool instanceof DesignTool) {
      return <DesignToolMenu tool={activeTool} />;
    }

    if (activeTool instanceof ReferencePointTransformTool) {
      return <ReferencePointTransform tool={activeTool} />;
    }

    if (activeTool instanceof VolumeInspectorTool) {
      // todo: Jest mock fails (cut-fill-dialog.test.tsx) having an import of CutFillMap in this file.
      return <CutFillMap />;
    }

    if (activeTool instanceof VolumeCalculatorTool) {
      return <VolumeCalculationTool />;
    }

    if (activeTool instanceof ReportTool) {
      return <ReportToolMenu />;
    }

    if (activeTool instanceof OnDemandTool) {
      return <OnDemandMenu />;
    }

    if (activeTool instanceof WmsTool) {
      return <WmsToolMenu wmsTool={activeTool} />;
    }

    if (activeTool instanceof OrthophotoComparisonTool) {
      return <CompareOrthophotosMenu tool={activeTool} />;
    }

    if (activeTool instanceof InspectionTool) {
      return <InspectionToolMenu tool={activeTool} />;
    }

    return undefined;
  }, [activeTool]);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const menuWindow = useRightMenuWindow(content && activeTool, contentRef);
  // Closed while the menu is in a window of its own.
  const expanded = content !== undefined && !menuWindow.inWindow;

  publish(SubscriptionTopic.RightMenuToggled, expanded);

  return (
    <Component className={props.className} data-expanded={expanded} ref={ref}>
      <Content data-expanded={expanded} ref={contentRef} />
      {menuWindow.render(<LanguageProvider>{content}</LanguageProvider>)}
    </Component>
  );
};

const Component = styled.div.attrs<{ 'data-expanded': boolean }>((props) => ({
  style: {
    width: props['data-expanded'] ? `${rightMenuWidth}px` : '0px',
  },
}))`
  ${reset}

  position: relative;
  overflow-y: auto;
  font-size: 14px;

  height: 100%;
  background-color: white;
  transition: width 0.5s ease-in-out;
  box-shadow:
    0 1px 3px rgba(0, 0, 0, 0.12),
    0 1px 2px rgba(0, 0, 0, 0.24);

  // todo: Bad UX - remove and let user select text. Apply this where necessary.
  user-select: none;
`;

const Content = styled.div<{ 'data-expanded': boolean }>`
  position: absolute;
  right: 0;
  width: ${rightMenuWidth}px;
  height: 100%;
  overflow-x: hidden;

  > * {
    transition: 0s visibility;
    visibility: hidden;
    height: 100%;
    width: 100%;

    ${(props) =>
      props['data-expanded'] &&
      css`
        visibility: visible;
        transition-delay: 0.1s;
      `}
  }
`;

export { RightMenuAutoExpandContainer };
